import { InfiniteData, QueryClient, QueryKey } from "@tanstack/react-query";
import { ThreadMessagesQuery } from "@/gql/graphql";
import { AuthOrganisation, AuthUser } from "@/lib/Auth/authApi";

type Draft = {
  threadId: string;
  body?: string | null;
  author: AuthUser;
  organisation: AuthOrganisation | null;
  isAdminNote: boolean;
};

/**
 * Puts what was just written at the head of the loaded page, so the composer
 * clears against a timeline that already shows it. Returns the pages as they
 * were, for the caller to put back if the write fails.
 */
export function insertOptimisticMessage(
  queryClient: QueryClient,
  messagesQueryKey: QueryKey,
  draft: Draft,
) {
  const previous =
    queryClient.getQueryData<InfiniteData<ThreadMessagesQuery>>(
      messagesQueryKey,
    );

  const optimisticMessage = {
    id: `optimistic-${crypto.randomUUID()}`,
    thread_id: draft.threadId,
    user_id: draft.author.id,
    organisation_id: draft.organisation?.id ?? null,
    body: draft.body ?? null,
    is_admin_note: draft.isAdminNote,
    created_at: new Date().toISOString(),
    user: {
      id: draft.author.id,
      name: draft.author.name,
      profilePicture: null,
    },
    organisation: draft.organisation
      ? {
          id: draft.organisation.id,
          name: draft.organisation.name,
          profilePicture: draft.organisation.profile_picture_url
            ? { url: draft.organisation.profile_picture_url }
            : null,
        }
      : null,
    attachments: [],
  };

  queryClient.setQueryData<InfiniteData<ThreadMessagesQuery>>(
    messagesQueryKey,
    (old) => {
      if (!old || old.pages.length === 0) return old;
      const [firstPage, ...rest] = old.pages;
      if (!firstPage.threadMessages) return old;
      return {
        ...old,
        pages: [
          {
            ...firstPage,
            threadMessages: {
              ...firstPage.threadMessages,
              data: [
                optimisticMessage as any,
                ...firstPage.threadMessages.data,
              ],
            },
          },
          ...rest,
        ],
      };
    },
  );

  return { previous };
}
